import React, { useCallback, useMemo, useRef, useState } from "react";

import { useFps } from "@/contexts/FpsContext";
import { useVideoSegmentation } from "@utils/videoSegmentation";
import { twClassNames } from "@scripts/react/helpers/twClassNames";
import { TimelineInferenceProgressOverlay } from "./TimelineInferenceProgressOverlay";
import { useFlowState } from "@/hooks/useFlowState";
import { useVideoTimelinePreviews } from "@/hooks/useVideoTimelinePreviews";
import { ThumbnailMode } from "@utils/videoEditing";
import { PreviewThumbnails } from "../Preview/PreviewThumbnails";
import { MediaItem } from "@/types/media";
interface VideoControlsTimelineProps {
    videoRef?: React.RefObject<HTMLVideoElement | null>;
    mediaItem?: MediaItem;
    flowLocked: boolean;
    onPauseClicked?: (currentTime: number) => void;
    onFramePreviewsReady?: () => void;
    currentTime: number;
    setCurrentTime: (time: number) => void;
    isVideoReadyForSeeking: boolean;
    setIsVideoReadyForSeeking: (ready: boolean) => void;
    onTimelineClick?: (targetTime: number) => void;
    isDragging: boolean;
    setIsDragging: React.Dispatch<React.SetStateAction<boolean>>;
    hoverThrottleRef: React.RefObject<number | null>;
}

function formatTime(seconds: number): string {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    const formattedSeconds = remainingSeconds.toFixed(2).padStart(5, "0");
    return `${minutes}:${formattedSeconds}`;
}

const SLIDER_GRANULARITY_MULTIPLIER = 10;

const THUMBNAIL_GENERATOR_OPTS = {
    size: 128,
    quality: 6,
    mode: ThumbnailMode.ACCURATE,
    numPreviews: 8,
    defaultFramePreviews: Array(8).fill(undefined)
};

export const VideoControlsTimeline: React.FC<VideoControlsTimelineProps> = ({
    videoRef,
    mediaItem,
    flowLocked,
    onPauseClicked,
    onFramePreviewsReady,
    currentTime,
    setCurrentTime,
    isVideoReadyForSeeking,
    setIsVideoReadyForSeeking,
    onTimelineClick,
    isDragging,
    setIsDragging,
    hoverThrottleRef
}) => {
    const { hasFullVideoInference, step } = useFlowState();
    const [hoverPositionX, setHoverPositionX] = useState<number | null>(null);

    const timelineAreaRef = useRef<HTMLDivElement>(null);
    const lastSeekTimeRef = useRef<number>(0); // Track last seek time to prevent conflicts

    // Use inference FPS for timeline calculations to match prediction segments
    // This ensures timeline clicks align with the FPS used during inference
    const { fps: inferenceFps } = useFps();
    const videoFps = inferenceFps || mediaItem?.metadata?.videoMetadata?.fps || 30;

    const totalFrames = useMemo(
        () => mediaItem?.metadata?.frame_count || 0,
        [mediaItem?.metadata?.frame_count]
    );
    const fileDuration = useMemo(
        () => mediaItem?.metadata?.videoMetadata?.duration || 0,
        [mediaItem?.metadata?.videoMetadata?.duration]
    );
    const keyframeOffset = useMemo(
        () => mediaItem?.metadata?.keyframe_offset || 0,
        [mediaItem?.metadata?.keyframe_offset]
    );

    // Use the custom hook for preview generation
    const { orderedFramePreviews } = useVideoTimelinePreviews({
        mediaItem,
        totalFrames,
        fileDuration,
        keyframeOffset,
        onFramePreviewsReady,
        isVideoReadyForSeeking,
        setIsVideoReadyForSeeking,
        videoRef,
        thumbnailGeneratorOpts: THUMBNAIL_GENERATOR_OPTS
    });

    const { secondsPerFrame, segmentForTargetTime, clampedTimeForSegment, frameCount } =
        useVideoSegmentation(fileDuration, videoFps);

    // Adjust frame calculations for keyframe offset
    // For trimmed videos, we need to calculate frames relative to the effective start time
    const adjustedCurrentTime = Math.max(0, currentTime - keyframeOffset);

    const currentGranularFrameForSlider = Math.min(
        Math.floor(adjustedCurrentTime * videoFps * SLIDER_GRANULARITY_MULTIPLIER),
        frameCount > 0 ? (frameCount - 1) * SLIDER_GRANULARITY_MULTIPLIER : 0
    );

    const currentActualFrame = Math.min(
        Math.floor(adjustedCurrentTime * videoFps),
        frameCount > 0 ? frameCount - 1 : 0
    );

    const updateCurrentTimeFromTimelineInteraction = useCallback(
        (granularFrame: number) => {
            const segment = Math.round(granularFrame / SLIDER_GRANULARITY_MULTIPLIER);
            const relativeTime = clampedTimeForSegment(segment);
            // Add keyframe offset to get absolute time for video element
            const absoluteTime = relativeTime + keyframeOffset;

            if (videoRef?.current) videoRef.current.currentTime = absoluteTime;
            if (isDragging) setCurrentTime(absoluteTime);
        },
        [clampedTimeForSegment, videoRef, isDragging, setCurrentTime, keyframeOffset]
    );

    const handleTimelineChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            if (flowLocked) return;

            updateCurrentTimeFromTimelineInteraction(parseInt(e.target.value, 10));
        },
        [flowLocked, updateCurrentTimeFromTimelineInteraction]
    );

    const handleTimelineInput = useCallback(
        (e: React.FormEvent<HTMLInputElement>) => {
            if (flowLocked) return;

            updateCurrentTimeFromTimelineInteraction(parseInt(e.currentTarget.value, 10));
        },
        [flowLocked, updateCurrentTimeFromTimelineInteraction]
    );

    const handleTimelineMouseDown = useCallback(() => {
        if (flowLocked) return;
        setIsDragging(true);
    }, [flowLocked, setIsDragging]);

    const handleTimelineMouseUp = useCallback(() => {
        if (flowLocked) return;
        setIsDragging(false);
        onPauseClicked?.(currentTime);
    }, [flowLocked, currentTime, onPauseClicked, setIsDragging]);

    const handleTimelineTouchStart = useCallback(() => {
        if (flowLocked) return;
        setIsDragging(true);
    }, [flowLocked, setIsDragging]);

    const handleTimelineTouchEnd = useCallback(() => {
        if (flowLocked) return;
        setIsDragging(false);
        onPauseClicked?.(currentTime);
    }, [flowLocked, currentTime, onPauseClicked, setIsDragging]);

    const handleTimelineMouseMove = useCallback(
        (e: React.MouseEvent<HTMLDivElement>) => {
            if (flowLocked) return;
            if (!timelineAreaRef.current || !fileDuration || fileDuration === 0) {
                setHoverPositionX(null);
                return;
            }
            if (hoverThrottleRef.current) cancelAnimationFrame(hoverThrottleRef.current);
            hoverThrottleRef.current = requestAnimationFrame(() => {
                const rect = timelineAreaRef.current?.getBoundingClientRect();
                if (!rect) return;
                const offsetX = e.clientX - rect.left;
                const clampedOffsetX = Math.max(0, Math.min(offsetX, rect.width));
                if (rect.width === 0) {
                    setHoverPositionX(null);
                    return;
                }
                const percent = (clampedOffsetX / rect.width) * 100;
                setHoverPositionX(percent);
            });
        },
        [flowLocked, fileDuration, hoverThrottleRef]
    );

    const handleTimelineMouseLeave = useCallback(() => {
        if (hoverThrottleRef.current) cancelAnimationFrame(hoverThrottleRef.current);
        hoverThrottleRef.current = null;
        setHoverPositionX(null);
    }, [hoverThrottleRef]);

    const onClick = useCallback(
        (clientX: number) => {
            if (!timelineAreaRef.current || !fileDuration || isDragging) return;
            if (!videoRef?.current) return;

            const rect = timelineAreaRef.current.getBoundingClientRect();
            const offsetX = clientX - rect.left;
            const clampedOffsetX = Math.max(0, Math.min(offsetX, rect.width));
            if (rect.width === 0) return;

            const percentage = clampedOffsetX / rect.width;
            // Calculate relative time within the trimmed video duration
            let relativeTime = percentage * fileDuration;
            relativeTime = Math.max(0, Math.min(relativeTime, fileDuration));

            const segment = segmentForTargetTime(relativeTime);
            const clampedRelativeTime = clampedTimeForSegment(segment);

            if (clampedRelativeTime === undefined) return;

            // Convert relative time to absolute time by adding keyframe offset
            const absoluteTime = clampedRelativeTime + keyframeOffset;

            const video = videoRef.current;

            if (!isVideoReadyForSeeking) {
                video.muted = true;
                if (video.paused) {
                    video
                        .play()
                        .then(() => {
                            video.pause();
                            video.currentTime = absoluteTime;
                            setCurrentTime(absoluteTime);
                            setIsVideoReadyForSeeking(true);
                        })
                        .catch(() => {
                            video.currentTime = absoluteTime;
                            setCurrentTime(absoluteTime);
                            setIsVideoReadyForSeeking(true);
                        });
                } else {
                    video.currentTime = absoluteTime;
                    setCurrentTime(absoluteTime);
                    setIsVideoReadyForSeeking(true);
                }
            } else {
                video.currentTime = absoluteTime;
                setCurrentTime(absoluteTime);
                lastSeekTimeRef.current = Date.now();
                onTimelineClick?.(absoluteTime);
            }
        },
        [
            timelineAreaRef,
            fileDuration,
            isDragging,
            videoRef,
            segmentForTargetTime,
            clampedTimeForSegment,
            isVideoReadyForSeeking,
            setIsVideoReadyForSeeking,
            setCurrentTime,
            onTimelineClick,
            keyframeOffset
        ]
    );

    const handleTimelineClick = useCallback(
        (e: React.MouseEvent<HTMLDivElement>) => {
            if (flowLocked) return;

            onClick(e.clientX);
        },
        [flowLocked, onClick]
    );

    const handleTimelineTouch = useCallback(
        (e: React.TouchEvent<HTMLDivElement>) => {
            if (flowLocked) return;

            const touch = e.touches[0] || e.changedTouches[0];
            if (!touch) return;

            onClick(touch.clientX);
        },
        [flowLocked, onClick]
    );

    return (
        <div
            ref={timelineAreaRef}
            onMouseMove={handleTimelineMouseMove}
            onMouseLeave={handleTimelineMouseLeave}
            onClick={handleTimelineClick}
            onTouchEnd={handleTimelineTouch}
            className={twClassNames(
                "videoTimeline relative",
                flowLocked ? "pointer-events-none cursor-not-allowed opacity-50" : "cursor-pointer"
            )}
        >
            <PreviewThumbnails previews={orderedFramePreviews} />

            {fileDuration > 0 && (
                <>
                    {/* Preview thumbnails */}
                    <div
                        className="timestampContainer top-0 -translate-y-full"
                        style={{
                            left: `${frameCount > 1 ? (currentActualFrame / (frameCount - 1)) * 100 : 0}%`,
                            transform: `translateX(-50%) translateY(-24px)`
                        }}
                    >
                        <span className="timestamp">{formatTime(currentTime)}</span>
                    </div>
                    {hasFullVideoInference && mediaItem && (
                        <TimelineInferenceProgressOverlay mediaItem={mediaItem} />
                    )}
                    {hoverPositionX !== null && !isDragging && (
                        <div
                            className="cursorTimeIndicator pointer-events-none absolute bottom-0 top-0"
                            style={{ left: `${hoverPositionX}%`, transform: "translateX(-25%)" }}
                        />
                    )}
                    <input
                        type="range"
                        min={0}
                        max={frameCount > 0 ? (frameCount - 1) * SLIDER_GRANULARITY_MULTIPLIER : 0}
                        step={1}
                        value={currentGranularFrameForSlider}
                        onChange={handleTimelineChange}
                        onInput={handleTimelineInput}
                        onMouseDown={handleTimelineMouseDown}
                        onMouseUp={handleTimelineMouseUp}
                        onTouchStart={handleTimelineTouchStart}
                        onTouchEnd={handleTimelineTouchEnd}
                        style={{
                            WebkitTapHighlightColor: "transparent",
                            touchAction: "manipulation"
                        }}
                        className={twClassNames(
                            "group absolute inset-0 z-20 h-full w-full appearance-none rounded-lg bg-transparent focus:outline-none [&::-moz-range-thumb]:relative [&::-moz-range-thumb]:z-10 [&::-moz-range-thumb]:h-[calc(100%+16px)] [&::-moz-range-thumb]:w-1.5 [&::-moz-range-thumb]:translate-y-[-8px] [&::-moz-range-thumb]:appearance-none [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-gray-900 [&::-moz-range-thumb]:ring-[1px] [&::-moz-range-thumb]:ring-white/70 [&::-moz-range-thumb]:backdrop-blur [&::-moz-range-track]:h-full [&::-moz-range-track]:appearance-none [&::-webkit-slider-runnable-track]:h-full [&::-webkit-slider-runnable-track]:appearance-none [&::-webkit-slider-thumb]:relative [&::-webkit-slider-thumb]:z-10 [&::-webkit-slider-thumb]:h-[calc(100%+16px)] [&::-webkit-slider-thumb]:w-1.5 [&::-webkit-slider-thumb]:translate-y-[-8px] [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-gray-900 [&::-webkit-slider-thumb]:ring-[1px] [&::-webkit-slider-thumb]:ring-white/70 [&::-webkit-slider-thumb]:backdrop-blur",
                            flowLocked ? "cursor-not-allowed" : "cursor-grab active:cursor-grabbing"
                        )}
                    />
                </>
            )}
        </div>
    );
};
